import React, { useState, useCallback } from "react";

import MySkeleton from "components/ui/feedback/Skeleton";
import { useHttpClient } from "shared/hooks/http-hook";
import { useStatsFetch } from "features/admin/statistics/useStatsApi";
import {
  SectionCard,
  ErrorMessage,
  SimpleTable,
  Badge,
  StatCard,
  formatNumber,
} from "features/admin/statistics/StatComponents";

// Canlı takip teşhis sekmesi (super_admin): şirket → servis → sefer seçilir; mobil
// uygulamanın tracking breadcrumb logları (bg_start / bg_stop_active / app_state /
// send_skip / bg_alive) ve TravelLocation boşluk özeti gösterilir. Konum boşluğunun
// nedeni (görev öldürüldü mü, ping mi düşürüldü, sürücü ekrandan mı çıktı) buradan okunur.

const KIND_LABELS = {
  VEHICLE_ROUTE: "Öğrenci Servisi",
  PERSONEL_SERVICE: "Personel Servisi",
  BUNDLED_ROUTE: "Toplu Servis",
};

const TRAVEL_STATUS_LABELS = {
  1: "Başlamadı",
  2: "Yolda",
  3: "Okula Vardı",
  4: "Kurumda Hazırlık",
  5: "Kurumdan Çıktı",
  6: "Tamamlandı",
  7: "QR Bekleniyor",
};

const EVENT_META = {
  bg_start: { label: "BG Başlatma", color: "#e8f5e9", text: "#2e7d32" },
  bg_stop_active: { label: "BG Durduruldu (sefer aktifken!)", color: "#ffebee", text: "#c62828" },
  bg_alive: { label: "BG Yaşıyor", color: "#e3f2fd", text: "#0d47a1" },
  bg_task_error: { label: "BG Görev Hatası", color: "#ffebee", text: "#c62828" },
  app_state: { label: "Uygulama Durumu", color: "#f3e5f5", text: "#6a1b9a" },
  send_skip: { label: "Ping Düşürüldü", color: "#fff3e0", text: "#e65100" },
};

// payload.detail → insan okur açıklama. Bilinmeyen değerler olduğu gibi gösterilir.
const DETAIL_LABELS = {
  started: "Görev başlatıldı",
  restarted: "Görev yeniden başlatıldı (stop→start)",
  error: "Başlatma hatası",
  task_not_defined: "Görev tanımlı değil",
  bundled_unmount: "Toplu servis ekranından çıkıldı",
  route_unmount: "Servis ekranından çıkıldı",
  bundled_finish: "Toplu servis bitirildi",
  route_finish: "Servis bitirildi",
  qr_finish: "QR ile bitirildi",
  app_boot: "Uygulama açılışı (orphan temizliği)",
  unspecified: "Belirtilmemiş",
  active: "Ön plana geldi",
  background: "Arka plana geçti (kilit / başka uygulama)",
  fg_while_inactive: "FG ping, uygulama arka planda (beklenen)",
  bg_while_active: "BG ping, uygulama ön planda (beklenen)",
  missing_state: "Travel/rota bilgisi eksik",
  route_time: "Rota saat penceresi dışında",
  accuracy: "GPS doğruluğu zayıf (>400m)",
  status_not_active: "Sefer durumu aktif değil",
  send_error: "Gönderim hatası (ağ)",
};

const selectStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: "4px",
  border: "1px solid #ccc",
  fontSize: "14px",
  background: "#fff",
};

const formatGap = (seconds) => {
  if (seconds === null) return "-";
  if (seconds < 60) return `${seconds} sn`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} dk ${seconds % 60} sn`;
  return `${Math.floor(minutes / 60)} sa ${minutes % 60} dk`;
};

// payload'dan detail/ts/travelid dışındaki ekleri "k=v" olarak göster (source, accuracy vs.)
const extraInfo = (payload) => {
  if (!payload || typeof payload !== "object") return "";
  return Object.entries(payload)
    .filter(([key]) => !["detail", "ts", "travelid"].includes(key))
    .map(([key, value]) => `${key}=${value}`)
    .join("  ");
};

const TrackingDiagnostics = () => {
  const { sendRequest } = useHttpClient();
  const {
    data: listData,
    isLoading: isListLoading,
    error: listError,
  } = useStatsFetch("/admin/statistics/schools-companies");

  const [companyId, setCompanyId] = useState("");
  const [services, setServices] = useState([]);
  const [serviceKey, setServiceKey] = useState(""); // `${kind}|${id}`
  const [travels, setTravels] = useState([]);
  const [travelId, setTravelId] = useState("");
  const [result, setResult] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const companies = listData?.companies || [];

  const handleCompanyChange = useCallback(
    async (nextCompanyId) => {
      setCompanyId(nextCompanyId);
      setServices([]);
      setServiceKey("");
      setTravels([]);
      setTravelId("");
      setResult(null);
      setError(null);
      if (!nextCompanyId) return;
      setIsLoading(true);
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/admin/statistics/tracking/services?companyId=${nextCompanyId}`,
        );
        setServices(responseData?.services || []);
      } catch (err) {
        setError(err?.message || "Servisler alınamadı.");
      } finally {
        setIsLoading(false);
      }
    },
    [sendRequest],
  );

  const handleServiceChange = useCallback(
    async (nextServiceKey) => {
      setServiceKey(nextServiceKey);
      setTravels([]);
      setTravelId("");
      setResult(null);
      setError(null);
      if (!nextServiceKey) return;
      const [kind, serviceId] = nextServiceKey.split("|");
      setIsLoading(true);
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/admin/statistics/tracking/travels?kind=${kind}&serviceId=${serviceId}`,
        );
        setTravels(responseData?.travels || []);
      } catch (err) {
        setError(err?.message || "Seferler alınamadı.");
      } finally {
        setIsLoading(false);
      }
    },
    [sendRequest],
  );

  const handleTravelChange = useCallback(
    async (nextTravelId) => {
      setTravelId(nextTravelId);
      setResult(null);
      setError(null);
      if (!nextTravelId) return;
      setIsLoading(true);
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/admin/statistics/tracking/logs/${nextTravelId}`,
        );
        setResult(responseData);
      } catch (err) {
        setError(err?.message || "Loglar alınamadı.");
      } finally {
        setIsLoading(false);
      }
    },
    [sendRequest],
  );

  const summary = result?.locationSummary;
  const biggestGap = summary?.gaps?.[0];

  return (
    <div style={{ padding: "20px", maxWidth: "1400px", overflowY: "auto" }}>
      <h2 style={{ marginTop: 0, marginBottom: "5px" }}>Canlı Takip Teşhis</h2>
      <p style={{ color: "#666", fontSize: "13px", marginTop: 0, marginBottom: "20px" }}>
        Sürücü uygulamasının konum yaşam döngüsü logları (breadcrumb). Konum boşluklarının nedenini gösterir;
        veritabanında değişiklik yapmaz, sadece okur.
      </p>

      <ErrorMessage message={listError} />
      <ErrorMessage message={error} />

      <SectionCard title="Sefer Seçimi">
        {isListLoading && !listData ? (
          <MySkeleton lineCount={3} />
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px" }}>
            <div style={{ flex: "1 1 280px", minWidth: "240px" }}>
              <label style={{ fontSize: "13px", color: "#555" }}>Şirket</label>
              <select value={companyId} onChange={(e) => handleCompanyChange(e.target.value)} style={selectStyle}>
                <option value="">Şirket seçin...</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.isDemo ? " (Demo)" : ""}
                    {!c.isActive ? " (Pasif)" : ""}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ flex: "1 1 280px", minWidth: "240px" }}>
              <label style={{ fontSize: "13px", color: "#555" }}>Servis</label>
              <select
                value={serviceKey}
                onChange={(e) => handleServiceChange(e.target.value)}
                style={selectStyle}
                disabled={!companyId}
              >
                <option value="">Servis seçin...</option>
                {Object.keys(KIND_LABELS).map((kind) => {
                  const kindServices = services.filter((s) => s.kind === kind);
                  if (kindServices.length === 0) return null;
                  return (
                    <optgroup key={kind} label={KIND_LABELS[kind]}>
                      {kindServices.map((s) => (
                        <option key={s.id} value={`${s.kind}|${s.id}`}>
                          {s.name}
                          {!s.isActive ? " (Pasif)" : ""}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
            </div>

            <div style={{ flex: "1 1 280px", minWidth: "240px" }}>
              <label style={{ fontSize: "13px", color: "#555" }}>Sefer (gün)</label>
              <select
                value={travelId}
                onChange={(e) => handleTravelChange(e.target.value)}
                style={selectStyle}
                disabled={!serviceKey}
              >
                <option value="">Sefer seçin...</option>
                {travels.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.day} — {TRAVEL_STATUS_LABELS[t.status] || t.status}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </SectionCard>

      {isLoading && <MySkeleton lineCount={6} />}

      {result && !isLoading && (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "20px" }}>
            <StatCard
              label="Sefer Durumu"
              value={TRAVEL_STATUS_LABELS[result.travel?.status] || result.travel?.status}
              sub={result.travel?.day}
            />
            <StatCard label="Konum Noktası" value={summary?.count ?? 0} sub="TravelLocation kaydı" />
            <StatCard
              label="İlk / Son Nokta"
              value={summary?.first ? summary.first.split(" ")[1] || summary.first : "-"}
              sub={summary?.last ? `son: ${summary.last.split(" ")[1] || summary.last}` : ""}
            />
            <StatCard
              label="En Büyük Boşluk"
              value={biggestGap ? formatGap(biggestGap.seconds) : "-"}
              color={biggestGap && biggestGap.seconds > 300 ? "#c62828" : "#2e7d32"}
              sub={
                biggestGap ? `${biggestGap.from.split(" ")[1]} → ${biggestGap.to.split(" ")[1]}` : "boşluk yok (>60sn)"
              }
            />
          </div>

          {summary?.gaps?.length > 1 && (
            <SectionCard title="Konum Boşlukları (60 sn üzeri, en büyük 5)">
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {summary.gaps.map((gap, idx) => (
                  <Badge
                    key={idx}
                    color={gap.seconds > 300 ? "#ffebee" : "#fff3e0"}
                    textColor={gap.seconds > 300 ? "#c62828" : "#e65100"}
                  >
                    {gap.from.split(" ")[1]} → {gap.to.split(" ")[1]} ({formatGap(gap.seconds)})
                  </Badge>
                ))}
              </div>
            </SectionCard>
          )}

          <SectionCard title={`Takip Logları (${formatNumber(result.logs?.length || 0)} kayıt)`}>
            {(result.logs || []).length === 0 ? (
              <p style={{ color: "#999", margin: 0 }}>
                Bu sefer için tracking logu yok. Loglar yalnızca güncel mobil sürümü (breadcrumb destekli) kullanan
                sürücülerde oluşur.
              </p>
            ) : (
              <SimpleTable
                columns={[
                  {
                    key: "date",
                    label: "Zaman",
                    render: (row) => (
                      <span style={{ whiteSpace: "nowrap", fontFamily: "monospace", fontSize: "13px" }}>
                        {row.date}
                      </span>
                    ),
                  },
                  {
                    key: "type",
                    label: "Olay",
                    render: (row) => {
                      const meta = EVENT_META[row.type] || { label: row.type, color: "#f5f5f5", text: "#333" };
                      return (
                        <Badge color={meta.color} textColor={meta.text}>
                          {meta.label}
                        </Badge>
                      );
                    },
                  },
                  {
                    key: "detail",
                    label: "Detay",
                    render: (row) => {
                      const detail = row.payload?.detail;
                      return DETAIL_LABELS[detail] || detail || "-";
                    },
                  },
                  {
                    key: "extra",
                    label: "Ek Bilgi",
                    render: (row) => (
                      <span style={{ color: "#777", fontSize: "12px", fontFamily: "monospace" }}>
                        {extraInfo(row.payload)}
                      </span>
                    ),
                  },
                ]}
                rows={result.logs}
                rowKey={(row) => row.id}
                maxHeight="600px"
              />
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
};

export default TrackingDiagnostics;
